import React from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  AccessTime,
  PlayArrow,
  //   AccessTime as AccessTimeIcon,
  //   HourglassBottom as HourglassBottomIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useTimezone } from '../hooks/useTimezone';
import { useThemeContext } from '../Context/ThemeContext';

const statusChipPalette = {
  Success: { bgLight: '#D1FAE5', text: '#065F46', border: '#10B981' },
  Failed: { bgLight: '#FEE2E2', text: '#991B1B', border: '#EF4444' },
  Processing: { bgLight: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' },
  'Partial Success': { bgLight: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  Unknown: { bgLight: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' },
};

const runModeChipPalette = {
  'On Demand': { bgLight: '#D1FAE5', text: '#065F46', border: '#10B981' }, // green
  Scheduled: { bgLight: '#FEF3C7', text: '#92400E', border: '#F59E0B' }, // amber
  'Unknown Mode': { bgLight: '#F3F4F6', text: '#6B7280', border: '#D1D5DB' }, // gray
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  hover: {
    scale: 1.01,
    backgroundColor: 'rgba(79, 172, 254, 0.05)',
    transition: { duration: 0.2, ease: 'easeInOut' },
  },
};

function RunStatusTable({ runStatus }) {
  const { formatDate } = useTimezone();
  const { darkMode } = useThemeContext();
  // Render nothing unless we actually have a status
  if (!runStatus?.status) return null;

  const getStatusStyles = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return {
          label: 'Success',
          color: '#10B981',
          icon: <CheckCircle fontSize="small" sx={{ color: '#065F46' }} />,
        };
      case 'FAILED':
        return {
          label: 'Failed',
          color: '#EF4444',
          icon: <Error fontSize="small" sx={{ color: '#991B1B' }} />,
        };
      case 'ACTIVE':
      case 'PROCESSING':
        return {
          label: 'Processing',
          color: '#3B82F6',
          icon: <PlayArrow fontSize="small" sx={{ color: '#1E40AF' }} />,
        };
      case 'PARTIAL_SUCCESS':
        return {
          label: 'Partial Success',
          color: '#fc9105ff',
          icon: <PlayArrow fontSize="small" sx={{ color: '#92400E' }} />,
        };
      default:
        return {
          label: 'Unknown',
          color: '#9CA3AF',
          icon: <AccessTime fontSize="small" sx={{ color: '#6B7280' }} />,
        };
    }
  };

  const getRunModeStyles = (mode = 'ON_DEMAND') => {
    switch (mode?.toUpperCase()) {
      case 'ON_DEMAND':
        return {
          label: 'On Demand',
          color: '#10B981',
          icon: <PlayArrow fontSize="small" sx={{ color: '#065F46' }} />,
        };
      case 'AUTO_SCHEDULE':
        return {
          label: 'Scheduled',
          color: '#F59E0B',
          icon: <AccessTime fontSize="small" sx={{ color: '#92400E' }} />,
        };
      default:
        return {
          label: 'Unknown Mode',
          color: '#9CA3AF',
          icon: <Error fontSize="small" sx={{ color: '#6B7280' }} />,
        };
    }
  };

  const statusStyle = getStatusStyles(runStatus?.status);
  const runModeValue = runStatus?.is_scheduled ? 'AUTO_SCHEDULE' : 'ON_DEMAND';
  const runModeStyle = getRunModeStyles(runModeValue);

  const statusPalette =
    statusChipPalette[statusStyle?.label] || statusChipPalette['Unknown'];
  const runModePalette =
    runModeChipPalette[runModeStyle?.label] ||
    runModeChipPalette['Unknown Mode'];

  const startedText = runStatus?.start_time
    ? formatDate(runStatus.start_time, { includeTime: true })
    : '';
  const endedText = runStatus?.end_time
    ? formatDate(runStatus.end_time, { includeTime: true })
    : '';
  const successCount =
    typeof runStatus?.success_record_count === 'number'
      ? runStatus.success_record_count
      : '';
  const failedCount =
    typeof runStatus?.failure_record_count === 'number'
      ? runStatus.failure_record_count
      : '';

  const errorMessage = runStatus?.error_message;
  const skippedCount =
    typeof runStatus?.skipped_record_count === 'number'
      ? runStatus.skipped_record_count
      : '';

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        borderRadius: '8px',
        background: darkMode
          ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f9faff 100%)',
        border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.3)' : 'rgba(89, 101, 166, 0.2)'}`,
        p: 2,
        mb: 2,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: darkMode
          ? `
            0px 4px 12px rgba(89, 101, 166, 0.15),
            0px 8px 24px rgba(0, 0, 0, 0.3),
            0px 1px 3px rgba(0, 0, 0, 0.2)
          `
          : `
            0px 4px 12px rgba(89, 101, 166, 0.12),
            0px 8px 24px rgba(89, 101, 166, 0.08),
            0px 1px 3px rgba(89, 101, 166, 0.15)
          `,
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: darkMode
            ? 'linear-gradient(135deg, rgba(89,101,166,0.08) 0%, rgba(89,101,166,0.04) 100%)'
            : 'linear-gradient(135deg, rgba(89,101,166,0.04) 0%, rgba(89,101,166,0.02) 100%)',
          zIndex: 0,
        },
      }}
    >
      {errorMessage && runStatus?.status?.toUpperCase() !== 'SUCCESS' && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '8px',
            backgroundColor: darkMode ? 'rgba(254, 242, 242, 0.1)' : '#FEF2F2',
            border: `1px solid ${darkMode ? 'rgba(252, 165, 165, 0.3)' : '#FCA5A5'}`,
            color: darkMode ? '#FCA5A5' : '#B91C1C',
            fontWeight: 500,
            fontSize: '0.95rem',
            position: 'relative',
            zIndex: 1,
          }}
        >
          ⚠️ {errorMessage}
        </Box>
      )}

      <TableContainer
        sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: `1px solid ${darkMode ? 'rgba(89, 101, 166, 0.2)' : 'rgba(89, 101, 166, 0.15)'}`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Table size="small">
          <TableHead
            sx={{
              position: 'relative',
              background: darkMode
                ? 'linear-gradient(135deg, rgba(89,101,166,0.2) 0%, rgba(89,101,166,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(89,101,166,0.1) 0%, rgba(89,101,166,0.08) 100%)',
              boxShadow: darkMode
                ? 'inset 0 -1px 0 rgba(89,101,166,0.3), 0 1px 6px rgba(0,0,0,0.1)'
                : 'inset 0 -1px 0 rgba(89,101,166,0.2), 0 1px 6px rgba(0,0,0,0.05)',
              transition:
                'background .25s ease, box-shadow .25s ease, transform .15s ease',

              // header text default
              '& th': {
                color: darkMode ? '#E2E8F0' : '#1f2230',
                fontWeight: 600,
                transition:
                  'transform .2s ease, letter-spacing .2s ease, color .2s ease',
              },

              // separators with theme tint
              '& th:not(:last-of-type)': {
                borderRight: `1px solid ${darkMode ? 'rgba(89,101,166,0.3)' : 'rgba(89,101,166,0.2)'}`,
              },

              '&:hover th': {
                color: darkMode ? '#ffffff' : '#111827',
              },
            }}
          >
            <TableRow>
              {[
                'Flow Status',
                'Run Mode',
                'Started',
                'Ended',
                'Success Count',
                'Skip Count',
                'Failed Count',
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    py: 0.6,
                    color: darkMode ? '#E2E8F0' : '#374151',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            <motion.tr
              variants={rowVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
              component={TableRow}
              sx={{
                fontSize: '0.875rem',
                backgroundColor: darkMode ? 'transparent' : 'transparent',
                '&:nth-of-type(even)': {
                  backgroundColor: darkMode
                    ? 'rgba(89, 101, 166, 0.05)'
                    : 'rgba(89, 101, 166, 0.03)',
                },
                '&:hover': {
                  backgroundColor: darkMode
                    ? 'rgba(89, 101, 166, 0.1)'
                    : 'rgba(89, 101, 166, 0.05)',
                  transition: 'background-color 0.2s ease',
                },
              }}
            >
              {/* Flow Status */}
              <TableCell sx={{ py: 2 }}>
                <Chip
                  icon={statusStyle?.icon}
                  label={statusStyle?.label}
                  size="small"
                  sx={{
                    // ⬇️ switched to light style with colored text & border
                    backgroundColor: statusPalette.bgLight,
                    color: statusPalette.text,
                    border: `1px solid ${statusPalette.border}`,

                    fontWeight: 700,
                    height: 28,
                    borderRadius: '8px',
                    px: 1.2,
                    boxShadow:
                      'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                    '& .MuiChip-icon': {
                      /* keep white if you like; or match text: */
                      color: `${statusPalette.text} !important`,
                      opacity: 1,
                      mr: 0.75,
                    },
                    '& .MuiChip-icon svg': { fontSize: 25 },
                    '& .MuiChip-label': {
                      px: 0.5,
                      letterSpacing: '0.02em',
                      fontWeight: 700,
                    },
                  }}
                />
              </TableCell>

              <TableCell sx={{ py: 2 }}>
                <Chip
                  icon={runModeStyle?.icon}
                  label={runModeStyle?.label}
                  size="small"
                  sx={{
                    backgroundColor: runModePalette.bgLight,
                    color: runModePalette.text,
                    border: `1px solid ${runModePalette.border}`,

                    fontWeight: 700,
                    height: 28,
                    borderRadius: '10px',
                    px: 1.2,
                    boxShadow:
                      'inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(2,8,23,0.08)',
                    '& .MuiChip-icon': {
                      color: `${runModePalette.text} !important`,
                      opacity: 1,
                      mr: 0.75,
                    },
                    '& .MuiChip-icon svg': { fontSize: 25 },
                    '& .MuiChip-label': {
                      px: 0.5,
                      letterSpacing: '0.02em',
                      fontWeight: 700,
                    },
                  }}
                />
              </TableCell>

              {/* Started */}
              <TableCell
                sx={{
                  fontSize: '0.875rem',
                  color: darkMode ? 'rgba(226, 232, 240, 0.8)' : '#64748b',
                  py: 2,
                }}
              >
                {startedText && (
                  <>
                    {/* <AccessTimeIcon
                      fontSize="small"
                      sx={{ mr: 0.75, color: '#3B82F6', verticalAlign: 'middle' }}
                    /> */}
                    <span>{startedText}</span>
                  </>
                )}
              </TableCell>

              {/* Ended */}
              <TableCell
                sx={{
                  fontSize: '0.875rem',
                  color: darkMode ? 'rgba(226, 232, 240, 0.8)' : '#64748b',
                  py: 2,
                }}
              >
                {endedText && (
                  <>
                    {/* <HourglassBottomIcon
                      fontSize="small"
                      sx={{ mr: 0.75, color: '#3B82F6', verticalAlign: 'middle' }}
                    /> */}
                    <span>{endedText}</span>
                  </>
                )}
              </TableCell>

              {/* Success Count */}
              <TableCell
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#16A34A',
                  py: 2,
                }}
              >
                {successCount !== '' && (
                  <>
                    <span
                      role="img"
                      aria-label="success"
                      style={{ marginRight: 6 }}
                    >
                      ✅
                    </span>
                    {successCount}
                  </>
                )}
              </TableCell>

              <TableCell
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#F59E0B',
                  py: 2,
                }}
              >
                {skippedCount !== '' && (
                  <>
                    <span
                      role="img"
                      aria-label="skipped"
                      style={{ marginRight: 6 }}
                    ></span>
                    {skippedCount}
                  </>
                )}
              </TableCell>

              {/* Failed Count */}
              <TableCell
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#DC2626',
                  py: 2,
                }}
              >
                {failedCount !== '' && (
                  <>
                    <span
                      role="img"
                      aria-label="failed"
                      style={{ marginRight: 6 }}
                    >
                      ❌
                    </span>
                    {failedCount}
                  </>
                )}
              </TableCell>
            </motion.tr>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

RunStatusTable.propTypes = {
  runStatus: PropTypes.object,
};

export default RunStatusTable;
